import { useState } from "react";
import { api } from "../api.js";
import type { App } from "../store.js";

const PALETTE = [
  "#2f6fb0",
  "#2f7d63",
  "#b8860b",
  "#c0392b",
  "#7b4fbf",
  "#0f8b8d",
  "#d2691e",
  "#555f6b",
];

export function SettingsPanel({ app }: { app: App }) {
  const { user, categories, t, toast, refreshCategories, updateUser, closeEditor, setView, exportIcs, importIcs } = app;
  const [name, setName] = useState(user?.displayName ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyIcs, setBusyIcs] = useState(false);

  async function createLinkCode() {
    setBusy(true);
    try {
      const { code: c, expiresAt } = await api.createLinkCode();
      setCode(c);
      toast(t("link.ready"), "ok");
      void expiresAt;
    } catch {
      toast(t("error.generic"), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && closeEditor()}>
      <div className="dialog" role="dialog" aria-modal="true">
        <h2 className="dialog__title">{t("nav.settings")}</h2>

        <div className="field">
          <label htmlFor="s-name">{t("field.displayName")}</label>
          <input
            id="s-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="spacer" />
          <button
            className="btn"
            onClick={() => void updateUser({ displayName: name.trim() })}
          >
            {t("action.save")}
          </button>
        </div>

        <div className="field">
          <span className="muted">{t("settings.account")}</span>
          <p className="muted">{t("settings.codeHint")}</p>
          <button className="btn" onClick={() => void createLinkCode()} disabled={busy}>
            {t("link.create")}
          </button>
          {code && (
            <div className="attachment" style={{ marginBlockStart: 8 }}>
              <code style={{ fontSize: 20, letterSpacing: "0.2em" }}>{code}</code>
            </div>
          )}
        </div>

        <div className="field">
          <span className="muted">{t("settings.categories")}</span>
          <div className="attachments">
            {categories.map((c) => (
              <div className="attachment" key={c.id}>
                <span
                  className="swatch"
                  style={{ background: c.color, cursor: "default" }}
                  aria-hidden="true"
                />
                <span style={{ flex: 1 }}>{c.name}</span>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={async () => {
                    await api.deleteCategory(c.id);
                    await refreshCategories();
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
            <NewCategory
              t={t}
              palette={PALETTE}
              onCreate={async (cname, color) => {
                await api.createCategory(cname, color);
                await refreshCategories();
              }}
            />
          </div>
        </div>

        <div className="field">
          <span className="muted">{t("ics.hint")}</span>
          <div style={{ display: "flex", gap: 8, marginBlockStart: 8 }}>
            <button
              className="btn"
              onClick={() => void exportIcs()}
              disabled={busyIcs}
            >
              {t("ics.export")}
            </button>
            <label className="btn">
              {t("ics.import")}
              <input
                type="file"
                accept=".ics,text/calendar"
                style={{ display: "none" }}
                disabled={busyIcs}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  setBusyIcs(true);
                  void importIcs(file).finally(() => setBusyIcs(false));
                }}
              />
            </label>
          </div>
        </div>

        <div className="dialog__actions">
          <button
            className="btn"
            onClick={() => {
              closeEditor();
              setView("month");
            }}
          >
            {t("action.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewCategory({
  t,
  palette,
  onCreate,
}: {
  t: App["t"];
  palette: string[];
  onCreate: (name: string, color: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(palette[0]!);

  return (
    <div className="attachments__drop">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
        {palette.map((p) => (
          <button
            key={p}
            type="button"
            className="swatch"
            style={{ background: p }}
            aria-pressed={color === p}
            aria-label={p}
            onClick={() => setColor(p)}
          />
        ))}
      </div>
      <input
        className="input"
        style={{ marginBlockStart: 8 }}
        value={name}
        placeholder={t("action.addCategory")}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        className="btn btn--primary"
        style={{ marginBlockStart: 8 }}
        disabled={!name.trim()}
        onClick={async () => {
          await onCreate(name.trim(), color);
          setName("");
        }}
      >
        {t("action.add")}
      </button>
    </div>
  );
}
